import { useCallback, useEffect, useRef, useState } from "react";
import { animations, byId, byKey } from "../animations/registry";

// De motor achter de animaties: hij houdt bij wat er nú speelt en ruimt het
// vanzelf weer op. Meerdere animaties tegelijk mag - een verkoop is een
// banner én confetti én een oplichtende rij.
//
// Elke run krijgt een eigen runId, zodat dezelfde animatie twee keer kort na
// elkaar echt opnieuw begint in plaats van door te lopen (React hergebruikt
// anders hetzelfde element en de CSS-animatie herstart niet).

let counter = 0;

// Licht de rij of podiumtrede van één verkoper op. Die staan verspreid over
// drie kaarten, dus we zoeken ze op via data-name in plaats van de klasse
// door de hele componentenboom door te geven.
function highlightSeller(name, ms) {
  if (!name) return () => {};

  let nodes = [];
  // Eén frame wachten: bij een verkoop komen de nieuwe cijfers en de animatie
  // tegelijk binnen, en dan staat de ranglijst pas ná dat frame op de nieuwe
  // volgorde. Zoeken we eerder, dan lichten we de verkeerde rij op.
  const frame = requestAnimationFrame(() => {
    nodes = Array.from(
      document.querySelectorAll(`[data-name="${CSS.escape(name)}"]`),
    );
    nodes.forEach((node) => node.classList.add("fx-highlight"));
  });

  const clean = () => nodes.forEach((node) => node.classList.remove("fx-highlight"));
  const timer = setTimeout(clean, ms);

  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    clean();
  };
}

export function useAnimations({ keyboard = true } = {}) {
  const [active, setActive] = useState([]);
  const timers = useRef(new Map());

  const clear = useCallback((runId) => {
    const timer = timers.current.get(runId);
    if (timer) {
      clearTimeout(timer.id);
      timer.cleanup?.();
      timers.current.delete(runId);
    }
    setActive((list) => list.filter((run) => run.runId !== runId));
  }, []);

  const play = useCallback(
    (id, payload = {}) => {
      const animation = byId(id);
      if (!animation) return;

      const runId = ++counter;

      const cleanup =
        animation.target === "seller"
          ? highlightSeller(payload.name, animation.ms)
          : null;

      timers.current.set(runId, {
        id: setTimeout(() => clear(runId), animation.ms),
        cleanup,
      });
      setActive((list) => [...list, { runId, id, payload }]);
      return runId;
    },
    [clear],
  );

  const stopAll = useCallback(() => {
    timers.current.forEach((timer) => {
      clearTimeout(timer.id);
      timer.cleanup?.();
    });
    timers.current.clear();
    setActive([]);
  }, []);

  // Alles achter elkaar, met een korte pauze ertussen. Handig om in één keer
  // te zien wat er allemaal is.
  const playAll = useCallback(() => {
    stopAll();
    let wait = 0;
    animations.forEach((animation) => {
      // Alleen hier de voorbeeldgegevens: dit is de rondleiding, niet iets
      // wat uit echte cijfers komt.
      setTimeout(() => play(animation.id, animation.demo || {}), wait);
      wait += Math.min(animation.ms, 3000) + 400;
    });
  }, [play, stopAll]);

  useEffect(() => {
    if (!keyboard) return undefined;

    const onKey = (event) => {
      // Niet meepikken terwijl iemand in een veld typt.
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") return stopAll();
      if (event.key === " ") {
        event.preventDefault();
        return playAll();
      }

      const animation = byKey(event.key);
      if (animation) play(animation.id);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboard, play, playAll, stopAll]);

  // Bij het verlaten van het scherm alle timers opruimen.
  useEffect(() => () => stopAll(), [stopAll]);

  // Animaties die een bestaand onderdeel aansturen (de meter, het podium)
  // doen dat via een klasse op de buitenste div.
  const rootClass = active.map((run) => `anim-${run.id}`).join(" ");

  return { active, play, playAll, stopAll, rootClass };
}
